/**
 * 成果物（sandbox iframe）が渡した CSV を、内容に手を加えず全セル文字列の xlsx に変換する。
 *
 * 入力は信頼できない成果物由来のデータで、変換は親ページ（認証済みオリジン）で行う。
 * そのためセル値は必ず XML エスケープし、数式（`<f>`）は一切出力しない（`=HYPERLINK(...)` 等も
 * 文字列のまま保存され Excel で評価されない）。依存は既存の papaparse（CSV 解析）と
 * fflate（zip）のみ（docs/features/custom-dashboard-security.md の「ファイルダウンロード経路」）。
 */
import { Zip, ZipDeflate, strToU8 } from 'fflate';
import Papa from 'papaparse';

/** Excel のシートの行数上限。 */
export const XLSX_MAX_ROWS = 1_048_576;
/** Excel のシートの列数上限（XFD 列）。 */
export const XLSX_MAX_COLUMNS = 16_384;
/** Excel のセル 1 つに入る文字数の上限。 */
export const XLSX_MAX_CELL_LENGTH = 32_767;

/** CSV を xlsx に変換できないことを表す。message は閲覧者にそのまま表示する。 */
export class XlsxConversionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'XlsxConversionError';
  }
}

const XML_DECLARATION = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const SPREADSHEET_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const PACKAGE_RELATIONSHIPS_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';
const OFFICE_RELATIONSHIPS_NS =
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const SHEET_PATH = 'xl/worksheets/sheet1.xml';

/** シート以外のパーツ。拡張子を xlsx に固定し、マクロ（vbaProject）を含み得る構成にしない。 */
const STATIC_PARTS: ReadonlyArray<readonly [path: string, xml: string]> = [
  [
    '[Content_Types].xml',
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
      `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
      `<Default Extension="xml" ContentType="application/xml"/>` +
      `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
      `<Override PartName="/${SHEET_PATH}" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
      `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
      `</Types>`,
  ],
  [
    '_rels/.rels',
    `<Relationships xmlns="${PACKAGE_RELATIONSHIPS_NS}">` +
      `<Relationship Id="rId1" Type="${OFFICE_RELATIONSHIPS_NS}/officeDocument" Target="xl/workbook.xml"/>` +
      `</Relationships>`,
  ],
  [
    'xl/workbook.xml',
    `<workbook xmlns="${SPREADSHEET_NS}" xmlns:r="${OFFICE_RELATIONSHIPS_NS}">` +
      `<sheets><sheet name="Sheet1" sheetId="1" r:id="rId1"/></sheets>` +
      `</workbook>`,
  ],
  [
    'xl/_rels/workbook.xml.rels',
    `<Relationships xmlns="${PACKAGE_RELATIONSHIPS_NS}">` +
      `<Relationship Id="rId1" Type="${OFFICE_RELATIONSHIPS_NS}/worksheet" Target="worksheets/sheet1.xml"/>` +
      `<Relationship Id="rId2" Type="${OFFICE_RELATIONSHIPS_NS}/styles" Target="styles.xml"/>` +
      `</Relationships>`,
  ],
  [
    'xl/styles.xml',
    `<styleSheet xmlns="${SPREADSHEET_NS}">` +
      `<fonts count="1"><font><sz val="11"/><name val="游ゴシック"/><family val="3"/><charset val="128"/></font></fonts>` +
      `<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>` +
      `<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>` +
      `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
      `<cellXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/></cellXfs>` +
      `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>` +
      `</styleSheet>`,
  ],
];

// 巨大な CSV でもシート XML 全体を 1 本の文字列にしないよう、この長さごとに zip へ流す。
const SHEET_FLUSH_LENGTH = 1 << 20;

// Excel は文字列中の `_xHHHH_` を文字コードとしてデコードするため、元から含まれる表記は
// 先頭の `_` を `_x005F_` にして文字どおり表示させる（重なり `_x0041_x0042_` も先読みで拾う）。
const EXCEL_ESCAPE_LIKE = /_(?=x[0-9A-Fa-f]{4}_)/g;
// XML 1.0 で使えない文字。削除すると内容が変わるため Excel の `_xHHHH_` 表記で残す。
const XML_INVALID_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g;

const escapeCellText = (value: string): string =>
  value
    .replace(EXCEL_ESCAPE_LIKE, '_x005F_')
    .replace(
      XML_INVALID_CHARS,
      (char) => `_x${char.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')}_`,
    )
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const columnNames: string[] = [];
const columnNameOf = (index: number): string => {
  if (columnNames[index] === undefined) {
    let name = '';
    for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
      name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
    }
    columnNames[index] = name;
  }
  return columnNames[index];
};

/** 1 行分のセル XML を返す。空のセルは CSV を Excel で開いたときと同じく出力しない。 */
const rowCellsXml = (fields: readonly string[], rowNumber: number): string => {
  let xml = '';
  fields.forEach((value, column) => {
    if (value === '') return;
    if (column >= XLSX_MAX_COLUMNS) {
      throw new XlsxConversionError(
        `${rowNumber} 行目の列数が Excel の上限（${XLSX_MAX_COLUMNS.toLocaleString()} 列）を超えています`,
      );
    }
    const ref = `${columnNameOf(column)}${rowNumber}`;
    if (value.length > XLSX_MAX_CELL_LENGTH) {
      throw new XlsxConversionError(
        `${ref} の文字数が Excel のセルの上限（${XLSX_MAX_CELL_LENGTH.toLocaleString()} 文字）を超えています`,
      );
    }
    // Apple の Numbers / Quick Look は A 列以外から始まる行以降を表示しないため、値の無い A 列セルを置く。
    if (xml === '' && column > 0) xml = `<c r="A${rowNumber}"/>`;
    xml += `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${escapeCellText(value)}</t></is></c>`;
  });
  return xml;
};

const decodeUtf8 = (bytes: ArrayBuffer): string => {
  try {
    // 既定（ignoreBOM: false）で先頭の BOM は除かれる。
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    throw new XlsxConversionError('CSV が UTF-8 として読み取れません');
  }
};

/** CSV をシート XML として `write` に少しずつ渡す。最後の呼び出しだけ `final` が true。 */
const writeSheetXml = (csv: string, write: (xml: string, final: boolean) => void): void => {
  let pending = `${XML_DECLARATION}<worksheet xmlns="${SPREADSHEET_NS}"><sheetData>`;
  let rowNumber = 0;
  let failure: unknown;
  Papa.parse<string[]>(csv, {
    delimiter: ',',
    step: ({ data: fields }, parser) => {
      rowNumber += 1;
      try {
        const cells = rowCellsXml(fields, rowNumber);
        // 空行は行を作らない（末尾の改行による空行が行数上限に数えられないよう、判定はこの後）。
        if (cells === '') return;
        if (rowNumber > XLSX_MAX_ROWS) {
          throw new XlsxConversionError(
            `行数が Excel の上限（${XLSX_MAX_ROWS.toLocaleString()} 行）を超えています`,
          );
        }
        pending += `<row r="${rowNumber}">${cells}</row>`;
        if (pending.length >= SHEET_FLUSH_LENGTH) {
          write(pending, false);
          pending = '';
        }
      } catch (error) {
        failure = error;
        parser.abort();
      }
    },
  });
  if (failure !== undefined) throw failure;
  write(`${pending}</sheetData></worksheet>`, true);
};

const concatChunks = (chunks: readonly Uint8Array[]): Uint8Array<ArrayBuffer> => {
  const result = new Uint8Array(chunks.reduce((total, chunk) => total + chunk.length, 0));
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
};

/**
 * UTF-8 の CSV を 1 シートの xlsx に変換する。全セルを文字列（inlineStr）として格納し、
 * 数値化・日付化・数式化はしない。Excel の上限（行数・列数・セル文字数）を超える場合や
 * UTF-8 として読めない場合は `XlsxConversionError` を投げる。
 */
export const convertCsvToXlsx = (csv: ArrayBuffer): Uint8Array<ArrayBuffer> => {
  const text = decodeUtf8(csv);
  const chunks: Uint8Array[] = [];
  const zip = new Zip((error, chunk) => {
    if (error) throw error;
    chunks.push(chunk);
  });
  for (const [path, xml] of STATIC_PARTS) {
    const part = new ZipDeflate(path);
    zip.add(part);
    part.push(strToU8(XML_DECLARATION + xml), true);
  }
  const sheet = new ZipDeflate(SHEET_PATH);
  zip.add(sheet);
  writeSheetXml(text, (xml, final) => sheet.push(strToU8(xml), final));
  zip.end();
  return concatChunks(chunks);
};
